import { formatRupiah, ORDER_FLAGS, type OrderFlag } from '@safepos/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  type Column,
  DataTable,
  Modal,
  PageHeader,
  SelectField,
  TextField,
} from '@safepos/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';

interface Outlet {
  id: string;
  name: string;
}
interface OrderRow {
  id: string;
  outletId: string;
  orderNo: string;
  status: 'paid' | 'void';
  grandTotal: number;
  discountTotal: number;
  itemCount: number;
  method: string | null;
  employeeName: string | null;
  closedAt: string;
  flagged: boolean;
}
interface OrderDetail {
  id: string;
  orderNo: string;
  status: 'paid' | 'void';
  subtotal: number;
  discountTotal: number;
  service: number;
  tax: number;
  rounding: number;
  grandTotal: number;
  paidTotal: number;
  changeTotal: number;
  closedAt: string;
  voidReason: string | null;
  employeeName: string | null;
  discount: { reason: string | null; amount: number } | null;
  approvedById: string | null;
  flags: OrderFlag[];
  reviewedAt: string | null;
  reviewedByName: string | null;
  syncedAt: string | null;
  items: {
    id: string;
    name: string;
    variantName: string | null;
    qty: number;
    unitPrice: number;
    modifiersTotal: number;
    discount: number;
    lineNet: number;
    modifiers: { name: string }[];
  }[];
  payments: { method: string; amount: number; tendered: number; change: number }[];
}
interface ShiftRow {
  id: string;
  outletId: string;
  status: 'open' | 'closed';
  openedAt: string;
  closedAt: string | null;
  openedByName: string | null;
  closedByName: string | null;
  openingCash: number;
  expectedCash: number | null;
  countedCash: number | null;
  variance: number | null;
}
interface Summary {
  orders: number;
  voided: number;
  gross: number;
  discounts: number;
  service: number;
  tax: number;
  rounding: number;
  netSales: number;
  byMethod: Record<string, number>;
  cash: {
    opening: number;
    sales: number;
    cashIn: number;
    cashOut: number;
    expected: number;
    counted: number | null;
    variance: number | null;
  };
}

const fmt = (d: string | null) =>
  d ? new Date(d).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const dayStart = (offset = 0) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d;
};
const toInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function TransactionsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [from, setFrom] = useState(toInput(dayStart(-6)));
  const [to, setTo] = useState(toInput(dayStart(0)));
  const [status, setStatus] = useState('');
  const [outletId, setOutletId] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const outlets = useQuery({ queryKey: ['outlets'], queryFn: () => api.get<Outlet[]>('/outlets') });
  const params = new URLSearchParams({
    limit: '100',
    from: new Date(`${from}T00:00:00`).toISOString(),
    to: new Date(new Date(`${to}T00:00:00`).getTime() + 86_400_000).toISOString(),
  });
  if (status) params.set('status', status);
  if (outletId) params.set('outletId', outletId);
  if (q.trim()) params.set('q', q.trim());
  const list = useQuery({
    queryKey: ['orders', params.toString()],
    queryFn: () =>
      api.get<{ data: OrderRow[]; totals: { paidCount: number; paidSales: number } }>(
        `/orders?${params}`,
      ),
  });
  const detail = useQuery({
    queryKey: ['order', open],
    enabled: !!open,
    queryFn: () => api.get<OrderDetail>(`/orders/${open}`),
  });
  const review = useSubmit(async () => {
    await api.post(`/orders/${open}/review`, {});
    void qc.invalidateQueries({ queryKey: ['orders'] });
    void qc.invalidateQueries({ queryKey: ['order', open] });
  });
  const doVoid = useSubmit(async () => {
    await api.post(`/orders/${open}/void`, { reason: reason.trim() });
    setReason('');
    void qc.invalidateQueries({ queryKey: ['orders'] });
    void qc.invalidateQueries({ queryKey: ['order', open] });
  });

  const outletName = (id: string) => outlets.data?.find((o) => o.id === id)?.name ?? '—';
  const cols: Column<OrderRow>[] = [
    {
      header: 'No. transaksi',
      cell: (o) => (
        <button
          className="font-medium text-brand-700 hover:underline"
          onClick={() => setOpen(o.id)}
        >
          {o.orderNo}
        </button>
      ),
    },
    { header: 'Waktu', cell: (o) => fmt(o.closedAt) },
    { header: 'Outlet', cell: (o) => outletName(o.outletId) },
    { header: 'Kasir', cell: (o) => o.employeeName ?? '—' },
    { header: 'Item', cell: (o) => o.itemCount },
    {
      header: 'Total',
      className: 'text-right',
      cell: (o) => (
        <span className={o.status === 'void' ? 'text-slate-400 line-through' : 'font-medium'}>
          {formatRupiah(o.grandTotal)}
        </span>
      ),
    },
    {
      header: 'Status',
      cell: (o) => (
        <span className="flex gap-1">
          {o.status === 'void' ? (
            <Badge tone="danger">Batal</Badge>
          ) : (
            <Badge tone="success">Lunas</Badge>
          )}
          {o.flagged && <Badge tone="warning">Tinjau</Badge>}
        </span>
      ),
    },
  ];
  const t = list.data?.totals;
  return (
    <>
      <PageHeader title="Transaksi" subtitle="Riwayat penjualan seluruh outlet" />
      <Card>
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <TextField
            label="Dari"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <TextField
            label="Sampai"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <SelectField
            label="Outlet"
            value={outletId}
            onChange={(e) => setOutletId(e.target.value)}
          >
            <option value="">Semua</option>
            {outlets.data?.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </SelectField>
          <SelectField label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Semua</option>
            <option value="paid">Lunas</option>
            <option value="void">Batal</option>
          </SelectField>
          <TextField label="No. transaksi" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {t && (
          <p className="mb-3 text-sm text-slate-600">
            {t.paidCount.toLocaleString('id-ID')} transaksi lunas · penjualan{' '}
            <b>{formatRupiah(t.paidSales)}</b>
          </p>
        )}
        {list.error && <Alert>{(list.error as Error).message}</Alert>}
        {list.isLoading ? (
          'Memuat…'
        ) : (
          <DataTable
            columns={cols}
            rows={list.data?.data ?? []}
            empty="Tidak ada transaksi pada rentang ini."
          />
        )}
      </Card>

      <Modal
        open={!!open}
        title={detail.data?.orderNo ?? 'Transaksi'}
        onClose={() => setOpen(null)}
      >
        {detail.data && (
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-slate-600">
              <span>
                {fmt(detail.data.closedAt)} · {detail.data.employeeName ?? '—'}
              </span>
              {detail.data.status === 'void' ? (
                <Badge tone="danger">Dibatalkan</Badge>
              ) : (
                <Badge tone="success">Lunas</Badge>
              )}
            </div>
            <ul className="divide-y text-sm">
              {detail.data.items.map((i) => (
                <li key={i.id} className="flex justify-between gap-2 py-2">
                  <div>
                    {i.qty}× {i.name}
                    {i.variantName ? ` · ${i.variantName}` : ''}
                    {i.modifiers.length > 0 && (
                      <div className="text-xs text-slate-500">
                        + {i.modifiers.map((m) => m.name).join(', ')}
                      </div>
                    )}
                  </div>
                  <div>{formatRupiah(i.lineNet)}</div>
                </li>
              ))}
            </ul>
            <dl className="space-y-1 border-t pt-2 text-sm">
              <Row k="Subtotal" v={formatRupiah(detail.data.subtotal)} />
              {detail.data.discountTotal > 0 && (
                <Row
                  k={`Diskon${detail.data.discount?.reason ? ` (${detail.data.discount.reason})` : ''}`}
                  v={`−${formatRupiah(detail.data.discountTotal)}`}
                />
              )}
              {detail.data.service > 0 && <Row k="Service" v={formatRupiah(detail.data.service)} />}
              {detail.data.tax > 0 && <Row k="PPN" v={formatRupiah(detail.data.tax)} />}
              {detail.data.rounding !== 0 && (
                <Row k="Pembulatan" v={formatRupiah(detail.data.rounding)} />
              )}
              <div className="flex justify-between text-base font-semibold">
                <dt>Total</dt>
                <dd>{formatRupiah(detail.data.grandTotal)}</dd>
              </div>
              {detail.data.payments[0] && (
                <Row
                  k={`Tunai ${formatRupiah(detail.data.payments[0].tendered)}`}
                  v={`Kembali ${formatRupiah(detail.data.payments[0].change)}`}
                />
              )}
            </dl>
            {detail.data.approvedById && <Alert tone="info">Diskon besar disetujui Manajer.</Alert>}
            {detail.data.flags.length > 0 && (
              <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <b>{detail.data.reviewedAt ? 'Sudah ditinjau' : 'Perlu ditinjau'}</b>
                  {detail.data.syncedAt && (
                    <span className="text-xs text-slate-500">
                      Diterima server {fmt(detail.data.syncedAt)}
                    </span>
                  )}
                </div>
                <ul className="list-disc space-y-1 pl-5">
                  {detail.data.flags.map((f, i) => (
                    <li key={i}>{ORDER_FLAGS[f.code] ?? f.code}</li>
                  ))}
                </ul>
                {review.error && <Alert>{review.error}</Alert>}
                {detail.data.reviewedAt ? (
                  <p className="text-xs text-slate-600">
                    Ditinjau {detail.data.reviewedByName ?? '—'} · {fmt(detail.data.reviewedAt)}
                  </p>
                ) : (
                  can('orders:void') && (
                    <Button
                      variant="secondary"
                      disabled={review.busy}
                      onClick={() => void review.run()}
                    >
                      Tandai sudah ditinjau
                    </Button>
                  )
                )}
              </div>
            )}
            {detail.data.status === 'void' && (
              <Alert tone="info">Dibatalkan: {detail.data.voidReason}</Alert>
            )}
            {detail.data.status === 'paid' && can('orders:void') && (
              <form
                className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (confirm('Batalkan transaksi ini?')) void doVoid.run();
                }}
              >
                {doVoid.error && <Alert>{doVoid.error}</Alert>}
                <TextField
                  label="Batalkan transaksi — alasan"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="danger"
                  disabled={reason.trim().length < 3 || doVoid.busy}
                >
                  Batalkan transaksi
                </Button>
              </form>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

export function ShiftsPage() {
  const [open, setOpen] = useState<string | null>(null);
  const outlets = useQuery({ queryKey: ['outlets'], queryFn: () => api.get<Outlet[]>('/outlets') });
  const list = useQuery({
    queryKey: ['shifts'],
    queryFn: () => api.get<{ data: ShiftRow[] }>('/shifts?limit=100'),
  });
  const detail = useQuery({
    queryKey: ['shift', open],
    enabled: !!open,
    queryFn: () => api.get<{ shift: ShiftRow; summary: Summary }>(`/shifts/${open}`),
  });
  const outletName = (id: string) => outlets.data?.find((o) => o.id === id)?.name ?? '—';
  const cols: Column<ShiftRow>[] = [
    {
      header: 'Dibuka',
      cell: (s) => (
        <button
          className="font-medium text-brand-700 hover:underline"
          onClick={() => setOpen(s.id)}
        >
          {fmt(s.openedAt)}
        </button>
      ),
    },
    { header: 'Outlet', cell: (s) => outletName(s.outletId) },
    { header: 'Kasir', cell: (s) => s.openedByName ?? '—' },
    { header: 'Ditutup', cell: (s) => fmt(s.closedAt) },
    {
      header: 'Selisih kas',
      className: 'text-right',
      cell: (s) =>
        s.variance == null ? (
          '—'
        ) : (
          <span className={s.variance === 0 ? 'text-emerald-700' : 'font-medium text-amber-700'}>
            {s.variance === 0 ? 'Pas' : formatRupiah(s.variance)}
          </span>
        ),
    },
    {
      header: 'Status',
      cell: (s) =>
        s.status === 'open' ? <Badge tone="info">Berjalan</Badge> : <Badge>Selesai</Badge>,
    },
  ];
  const s = detail.data?.summary;
  return (
    <>
      <PageHeader title="Shift Kasir" subtitle="Buka/tutup kasir dan rekonsiliasi kas laci" />
      <Card>
        {list.isLoading ? (
          'Memuat…'
        ) : (
          <DataTable columns={cols} rows={list.data?.data ?? []} empty="Belum ada shift." />
        )}
      </Card>
      <Modal open={!!open} title="Laporan shift" onClose={() => setOpen(null)}>
        {s && detail.data && (
          <dl className="space-y-1 text-sm">
            <Row k="Kasir" v={detail.data.shift.openedByName ?? '—'} />
            <Row k="Transaksi / batal" v={`${s.orders} / ${s.voided}`} />
            <Row k="Penjualan kotor" v={formatRupiah(s.gross)} />
            <Row k="Diskon" v={`−${formatRupiah(s.discounts)}`} />
            <Row k="Service" v={formatRupiah(s.service)} />
            <Row k="PPN" v={formatRupiah(s.tax)} />
            <Row k="Pembulatan" v={formatRupiah(s.rounding)} />
            <div className="flex justify-between border-t pt-2 text-base font-semibold">
              <dt>Penjualan bersih</dt>
              <dd>{formatRupiah(s.netSales)}</dd>
            </div>
            <div className="pt-3 font-medium">Kas laci</div>
            <Row k="Modal awal" v={formatRupiah(s.cash.opening)} />
            <Row k="Penjualan tunai" v={formatRupiah(s.cash.sales)} />
            <Row k="Kas masuk" v={formatRupiah(s.cash.cashIn)} />
            <Row k="Kas keluar" v={`−${formatRupiah(s.cash.cashOut)}`} />
            <div className="flex justify-between font-semibold">
              <dt>Diharapkan</dt>
              <dd>{formatRupiah(s.cash.expected)}</dd>
            </div>
            {s.cash.counted != null && <Row k="Dihitung" v={formatRupiah(s.cash.counted)} />}
            {s.cash.variance != null && (
              <Row k="Selisih" v={s.cash.variance === 0 ? 'Pas' : formatRupiah(s.cash.variance)} />
            )}
          </dl>
        )}
      </Modal>
    </>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-600">{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}
